import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBeautyProduct } from '../../../beauty-shared/context/BeautyProductContext';
import { usesLooksCaptureChrome } from '../../../beauty-shared/config/saasModules';
import { renderGuestQrDataUrl } from '../../shared/guestQrCode';
import {
  buildMobileUploadPageUrl,
  claimMobileUploadImage,
  createMobileUploadSession,
} from '../../looks/utils/looksApi';
import { getSessionId } from '../utils/session';
import { useLooksPageCopy } from '../../looks/hooks/useLooksPageCopy';
import { getSalonAppSessionToken } from '../../salon/utils/salonAppSessionToken';
import { getFranchiseId } from '../../shared/franchiseFlowAuth';
import {
  readStoredFranchiseBrandLogo,
  readStoredFranchiseBrandName,
} from '../../shared/franchiseBrand';
import { shouldShowKioskPoweredBy } from '../../shared/kioskPoweredBy';
import '../styles/CameraCapturePage.css';

const CAPTURE_OPTIONS = [
  {
    id: 'camera',
    path: '/capture/preview/camera',
    title: 'Camera',
    description: 'Take a selfie or use the rear camera.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    ),
  },
  {
    id: 'upload',
    path: '/capture/preview/upload',
    title: 'Upload',
    description: 'Choose a clear photo from your device.',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
];

const PHONE_OPTION = {
  id: 'phone',
  title: 'Phone',
  description: 'Scan QR to capture or upload from your phone.',
  icon: (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
};

export default function CameraCapturePage() {
  const navigate = useNavigate();
  const { appPath, appBase } = useBeautyProduct();
  const { title: pageTitle, description: pageDescription } = useLooksPageCopy();
  const titleId = useId();
  const isLooks = usesLooksCaptureChrome(appBase);

  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneToken, setPhoneToken] = useState('');
  const [phoneQr, setPhoneQr] = useState('');
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneStatus, setPhoneStatus] = useState('');

  useEffect(() => {
    getSessionId();
  }, []);

  const closePhoneModal = useCallback(() => {
    setShowPhoneModal(false);
    setPhoneToken('');
    setPhoneQr('');
    setPhoneBusy(false);
    setPhoneError('');
    setPhoneStatus('');
  }, []);

  const openPhoneModal = useCallback(async () => {
    setShowPhoneModal(true);
    setPhoneBusy(true);
    setPhoneError('');
    setPhoneStatus('Creating secure link…');
    setPhoneQr('');
    setPhoneToken('');
    try {
      const session = await createMobileUploadSession({
        branding: {
          logo: readStoredFranchiseBrandLogo(),
          name: readStoredFranchiseBrandName(),
          powered_by: shouldShowKioskPoweredBy(),
        },
      });
      const pageUrl = buildMobileUploadPageUrl(session.pagePath, {
        appSessionToken: getSalonAppSessionToken(),
        franchiseId: getFranchiseId(),
      });
      const qr = await renderGuestQrDataUrl(pageUrl, 280);
      setPhoneToken(session.token);
      setPhoneQr(qr);
      setPhoneStatus('Scan with your phone to capture or upload');
    } catch (err) {
      setPhoneError(err.message || 'Could not start phone upload');
      setPhoneStatus('');
    } finally {
      setPhoneBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!showPhoneModal || !phoneToken) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const claimed = await claimMobileUploadImage(phoneToken);
        if (cancelled) return;
        if (claimed.status === 'waiting' || !claimed.image) return;
        closePhoneModal();
        navigate(appPath('/studio'), { state: { image: claimed.image } });
      } catch (_) {
        /* keep polling */
      }
    };
    const id = window.setInterval(() => {
      void tick();
    }, 2000);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [showPhoneModal, phoneToken, closePhoneModal, navigate, appPath]);

  const options = isLooks ? [...CAPTURE_OPTIONS, PHONE_OPTION] : CAPTURE_OPTIONS;

  const phoneModal =
    showPhoneModal && typeof document !== 'undefined'
      ? createPortal(
          <div className="capture-phone-modal" role="presentation" onClick={closePhoneModal}>
            <div
              className="capture-phone-modal__card"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="capture-phone-modal__close"
                aria-label="Close"
                onClick={closePhoneModal}
              >
                ×
              </button>
              <h3 id={titleId}>Phone upload</h3>
              <p>{phoneStatus || 'Scan to open the capture page on your phone.'}</p>
              <div className="capture-phone-modal__frame">
                {phoneBusy ? (
                  <span>Preparing…</span>
                ) : phoneQr ? (
                  <img src={phoneQr} alt="QR code for phone upload" />
                ) : (
                  <span>{phoneError || 'Link unavailable'}</span>
                )}
              </div>
              {phoneError && phoneQr ? <p className="capture-phone-modal__error">{phoneError}</p> : null}
              <p className="capture-phone-modal__hint">Waiting for photo from this session only…</p>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`capture-options-root${isLooks ? ' capture-options-root--looks' : ''}`}>
      <main className={`capture-options-main${isLooks ? ' capture-options-main--looks' : ''}`}>
        <div className={isLooks ? 'capture-options-sheet' : undefined}>
        {isLooks ? (
          <header className="capture-options-hero">
            <h1>{pageTitle}</h1>
            <p>{pageDescription}</p>
          </header>
        ) : null}
        <div className={`capture-options-grid${isLooks ? ' capture-options-grid--three' : ''}`}>
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="capture-option-card"
              onClick={() => {
                if (option.id === 'phone') {
                  void openPhoneModal();
                  return;
                }
                navigate(appPath(option.path));
              }}
            >
              <div className="capture-option-icon">{option.icon}</div>
              <div className="capture-option-copy">
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </div>
            </button>
          ))}
        </div>
        </div>
      </main>
      {phoneModal}
    </div>
  );
}
